import { formatDate, formatDateTime } from '@/lib/time';
import type { TrainingStage } from '@/features/document-editor/training-stage';
import { ACTIVE_COMPANY_LOST_TEXT, NO_COMPANY_TEXT } from '@/lib/company-error-text';

// Presentación pura de la etapa de capacitación en la ficha (G4, cierre F1): estado en
// palabras, fecha límite (la vigencia) y avance de lectura. También los textos de la
// reprogramación de vigencia. Cero enums crudos (UX-COPY-01).

export interface TrainingStageSummary {
  label: string;
  cls: 'b-ok' | 'b-info' | 'b-red' | 'b-mut';
  lines: string[];
}

function dueLine(t: TrainingStage): string[] {
  return t.dueDate === null ? [] : [`Fecha límite: ${formatDate(t.dueDate)} (entrada en vigencia)`];
}

/** G2-C: el LMS no reporta la capacitación abierta (reemplaza la línea de avance). */
function unreportedLines(t: TrainingStage): string[] | null {
  if (t.lmsUnreportedSince === null || t.lmsUnreportedReason === null) {
    return null;
  }
  const since = formatDateTime(t.lmsUnreportedSince);
  const what =
    t.lmsUnreportedReason === 'missing'
      ? 'El LMS no reporta este documento'
      : 'El LMS tiene otra versión de este documento';
  return [`${what} desde el ${since}.`, 'La etapa sigue abierta hasta que Calidad la cierre.'];
}

function progressLine(t: TrainingStage): string {
  return t.progress === null
    ? 'Avance no disponible por ahora'
    : `${t.progress.completed} de ${t.progress.assigned} personas leyeron`;
}

/** Se cerró sin personas: si después aparecieron lecturas en el LMS, el avance real. */
function noRecipientsLines(t: TrainingStage): string[] {
  if (t.progress === null || t.progress.assigned === 0) {
    return ['La versión no tiene lecturas asignadas en el LMS.'];
  }
  const { assigned, completed } = t.progress;
  const given = assigned === 1 ? 'se asignó 1 lectura' : `se asignaron ${assigned} lecturas`;
  const done = completed === 1 ? '1 completa' : `${completed} completas`;
  return [
    'La capacitación se cerró porque la versión no tenía lecturas asignadas.',
    `Después ${given} en el LMS: ${done}.`,
  ];
}

export function trainingStageSummary(t: TrainingStage): TrainingStageSummary {
  switch (t.state) {
    case 'not_started':
      return {
        label: 'Pendiente de firma',
        cls: 'b-mut',
        lines: ['La capacitación empieza al firmarse la aprobación.'],
      };
    case 'in_progress':
      return {
        label: t.overdue ? 'Capacitación vencida' : 'En capacitación',
        cls: t.overdue ? 'b-red' : 'b-info',
        lines: [...dueLine(t), ...(unreportedLines(t) ?? [progressLine(t)])],
      };
    case 'completed':
      return {
        label: 'Capacitación completada',
        cls: 'b-ok',
        lines: [...dueLine(t), ...(t.progress === null ? [] : [progressLine(t)])],
      };
    case 'not_applicable':
      return {
        label: 'Sin personas a capacitar',
        cls: 'b-mut',
        lines: noRecipientsLines(t),
      };
    case 'superseded':
      return {
        label: 'Cerrada por una versión nueva',
        cls: 'b-mut',
        lines: ['Las lecturas pendientes pasaron a la versión aprobada después.'],
      };
    case 'retired':
      return {
        label: 'Cerrada por la baja del documento',
        cls: 'b-mut',
        lines: ['El documento se dio de baja antes de completar la capacitación.'],
      };
    case 'closed_by_quality':
      return {
        label: 'Cerrada por Calidad',
        cls: 'b-mut',
        lines: [
          `Calidad la cerró el ${formatDateTime(t.closedAt)} sin el reporte del LMS.`,
          'No cuenta como capacitación completada.',
        ],
      };
    case 'unknown':
      return {
        label: 'Sin registro de capacitación',
        cls: 'b-mut',
        lines: [
          'El documento se aprobó antes de que el QMS registrara esta etapa. El avance de lectura se consulta en el LMS.',
        ],
      };
  }
}

const RESCHEDULE_ERRORS: Readonly<Record<string, string>> = {
  effective_date_in_past: 'La fecha no puede ser anterior a hoy.',
  effective_date_invalid: 'La fecha no es válida.',
  effective_date_today_unavailable:
    'Por ahora la vigencia solo se puede reprogramar a partir de mañana.',
  not_reschedulable: 'El documento ya no admite reprogramación: está vigente o no está firmado.',
};

/** Error accionable de la reprogramación según el motivo del backend. */
export function rescheduleErrorText(reason: string | undefined): string {
  return (
    (reason === undefined ? undefined : RESCHEDULE_ERRORS[reason]) ??
    'No se pudo reprogramar la vigencia. Intenta de nuevo.'
  );
}

/** Aviso específico de éxito (UX-COPY-01): documento y fecha nueva. */
export function rescheduleToast(code: string, date: string, lmsSynced: boolean): string {
  const base = `Vigencia de ${code} reprogramada al ${formatDate(date)}`;
  return lmsSynced ? base : `${base}. El LMS se actualizará en unos minutos.`;
}

const CLOSE_ERRORS: Readonly<Record<string, string>> = {
  training_not_open: 'La etapa ya estaba cerrada; la ficha se actualizó.',
  document_retired:
    'El documento está dado de baja: su capacitación se cierra con la baja en los próximos minutos.',
  active_company_forbidden: ACTIVE_COMPANY_LOST_TEXT,
  no_active_company: NO_COMPANY_TEXT,
};

/** Rechazos que significan que la etapa ya cambió en el servidor: se recarga la tarjeta. */
export const CLOSE_STALE_REASONS: ReadonlySet<string> = new Set([
  'training_not_open',
  'document_retired',
]);

/** Error accionable del cierre de la etapa según el motivo y el estado HTTP (G2-C). */
export function closeTrainingErrorText(reason: string | undefined, status?: number): string {
  const known = reason === undefined ? undefined : CLOSE_ERRORS[reason];
  if (known !== undefined) {
    return known;
  }
  if (status === 403) {
    return 'No tienes permiso para cerrar la etapa de capacitación.';
  }
  if (status === 400) {
    return 'Revisa el motivo: debe tener entre 10 y 500 caracteres.';
  }
  if (status === 404) {
    return 'Este documento ya no está a tu alcance: revisa la empresa activa en el encabezado o tu acceso al tipo de documento.';
  }
  return 'No se pudo cerrar la etapa. Intenta de nuevo.';
}

/** Aviso específico de éxito (UX-COPY-01). */
export function closeTrainingToast(code: string): string {
  return `Etapa de capacitación de ${code} cerrada`;
}
